/**
 * Branded error messaging for Statlign.
 *
 * Every user-facing error on the platform should be routed through
 * `brandError()` so the copy consistently refers to Statlign instead of
 * leaking raw backend / Postgres / network error strings.
 */

export const BRAND = "Statlign";

/** Known backend error codes → friendly, branded copy. */
const KNOWN_MESSAGES: Record<string, string> = {
  NOT_AUTHENTICATED: `Please sign in to Statlign to continue.`,
  INSUFFICIENT_CREDITS: `You don't have enough credits. Top up in your Statlign wallet to reveal this prediction.`,
  INSUFFICIENT_COINS: `You don't have enough credits. Top up in your Statlign wallet to reveal this prediction.`,
  AGE_RESTRICTION: `Statlign is for users aged 18 and over.`,
  PREDICTION_WITHOUT_STRATEGY: `Statlign couldn't build a qualifying prediction for this match.`,
  token_revoked: `Your Statlign session expired. Please sign in again.`,
  bad_jwt: `Your Statlign session expired. Please sign in again.`,
};

function rawMessage(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (err instanceof Error) return err.message;
  if (typeof err === "object") {
    const anyErr = err as Record<string, unknown>;
    return (
      (anyErr["message"] as string) ||
      (anyErr["error_description"] as string) ||
      (anyErr["error"] as string) ||
      ""
    );
  }
  return "";
}

/**
 * Convert any error into a branded, user-friendly message.
 * @param err     the caught error
 * @param fallback context-specific fallback (already branded ideally)
 */
export function brandError(
  err: unknown,
  fallback = `${BRAND} ran into a problem. Please try again in a moment.`,
): string {
  const raw = rawMessage(err).trim();

  // Match known codes/phrases
  for (const [key, friendly] of Object.entries(KNOWN_MESSAGES)) {
    if (raw.includes(key)) return friendly;
  }

  // Network-ish failures
  if (/network|fetch|failed to fetch|timeout|offline/i.test(raw)) {
    return `${BRAND} couldn't reach the server. Check your connection and try again.`;
  }
  if (/permission|rls|not authorized|403|401/i.test(raw)) {
    return `You don't have access to that on ${BRAND}. Please sign in with the right account.`;
  }
  if (/rate limit|429|too many/i.test(raw)) {
    return `${BRAND} is handling a lot of requests right now — please try again shortly.`;
  }

  // If the raw message is short and human-readable, keep it but brand it.
  if (raw && raw.length < 120 && !/[{}[\]<>]|error:/i.test(raw)) {
    return raw;
  }

  return fallback;
}

/** Convenience for building a branded toast payload. */
export function brandErrorToast(err: unknown, title = `${BRAND} couldn't do that`) {
  return {
    title,
    description: brandError(err),
    variant: "destructive" as const,
  };
}
